import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { PORTFOLIO_TABS_NAME } from '../../../constants/reports/reports-data';
import { GlobalContext } from '../../../providers/global-provider';
import { PNLContext } from '../../../providers/pnl-provider';
import {
  DATA_DISPLAY_KEYS,
  PortfolioManagementContext,
} from '../../../providers/portfolio-management';
import { PositionsContext } from '../../../providers/positions/positions-provider';
import { RiskFiltersContext } from '../../../providers/risk-management/risk-filters-provider';
import {
  RiskManagementContext,
  riskTypes,
} from '../../../providers/risk-management/risk-management-provider';
import { StrikeReportContext } from '../../../providers/strike-report';
import { TransactionsContext } from '../../../providers/transactions/transactions-provider';

import { useFreezeSpotValues } from './use-freeze-spot-values';

export const usePortfolioCalculateAggregatedAccounts = () => {
  const [isAccountCalculatedAggregatedAccounts, setIsAccountCalculatedAggregatedAccounts] =
    useState(false);
  const [triggerRecalculate, setTriggerRecalculate] = useState(false);
  const [
    isReadyToDisplayTabsForTheFirstTimeOrRecalculate,
    setIsReadyToDisplayTabsForTheFirstTimeOrRecalculate,
  ] = useState(false);
  const { portfolioCurrentTab } = useContext(GlobalContext);
  const {
    filters: { riskToken },
  } = useContext(RiskFiltersContext);
  const {
    aggregatedData,
    isPortfolioLoading,
    selectedDataDisplayOption,
    selectedAccountsAggregated,
    setAggregatedCalculationStatus,
    riskSettingsSelected,
    clearAggregatedData,
    spotOverrideValue,
  } = useContext(PortfolioManagementContext);
  const { getData: getDataSpot } = useContext(RiskManagementContext);
  const {
    getDataPnl,
    filters: { pnlRiskToken },
  } = useContext(PNLContext);
  const { getData: getDataPositionsAndOrders } = useContext(PositionsContext);
  const { getData: getDataTransactions } = useContext(TransactionsContext);
  const { getStrikeReportData } = useContext(StrikeReportContext);
  const { updateFreezeSpotMapAndCallGetData } = useFreezeSpotValues();

  const isSpotCalculateAggregatedAccounts = true;

  const isDataDisplayPerAccount = selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount;

  const filterAccountsByExchanges = (accounts: any[], exchanges: string[]) => {
    return accounts.reduce((acc: any, account: any) => {
      const exchange = account.exchange;

      if (exchanges.includes(exchange)) {
        if (!acc[exchange]) {
          acc[exchange] = [];
        }
        acc[exchange].push(account);
      }
      return acc;
    }, {});
  };

  const aggregatedAccountsByExchange = useMemo(() => {
    return filterAccountsByExchanges(selectedAccountsAggregated, ['deribit', 'okx', 'otc']);
  }, [selectedAccountsAggregated]);

  const isUpdateFreezeSpotMap =
    riskSettingsSelected.isFreezeSpotOverride && spotOverrideValue !== '';

  const handleDataFetch = useCallback(
    (type: any) => {
      switch (type) {
        case PORTFOLIO_TABS_NAME.spotLadder:
        case PORTFOLIO_TABS_NAME.spotGrid:
        case PORTFOLIO_TABS_NAME.spotTime: {
          const riskTypeMapping = {
            [PORTFOLIO_TABS_NAME.spotLadder]: riskTypes.spotLadder,
            [PORTFOLIO_TABS_NAME.spotGrid]: riskTypes.spotGrid,
            [PORTFOLIO_TABS_NAME.spotTime]: riskTypes.spotTime,
          };
          const riskType = riskTypeMapping[type];

          if (isUpdateFreezeSpotMap) {
            updateFreezeSpotMapAndCallGetData({
              riskType,
            });
          } else {
            Object.keys(aggregatedAccountsByExchange).forEach((exchange) => {
              const accounts = aggregatedAccountsByExchange[exchange];
              if (accounts.length > 0) {
                getDataSpot({
                  account: accounts,
                  dataParent: riskType,
                  riskTokenParam: riskToken,
                  riskSettings: riskSettingsSelected,
                  isSpotAggregatedAccounts: isSpotCalculateAggregatedAccounts,
                  accountsAggregated: selectedAccountsAggregated,
                });
              }
            });
          }
          break;
        }

        case PORTFOLIO_TABS_NAME.pnl:
          getDataPnl({
            accounts: selectedAccountsAggregated,
            periodDate: [],
            riskToken: pnlRiskToken.toLowerCase(),
          });
          break;

        case PORTFOLIO_TABS_NAME.positionsAndOrders:
          Object.keys(aggregatedAccountsByExchange).forEach((exchange) => {
            const accounts = aggregatedAccountsByExchange[exchange];
            if (accounts.length > 0) {
              getDataPositionsAndOrders(accounts, selectedAccountsAggregated);
            }
          });
          break;

        case PORTFOLIO_TABS_NAME.transactions:
          Object.keys(aggregatedAccountsByExchange).forEach((exchange) => {
            const accounts = aggregatedAccountsByExchange[exchange];
            if (accounts.length > 0 && exchange === 'deribit') {
              getDataTransactions(accounts, selectedAccountsAggregated);
            }
          });
          break;
        case PORTFOLIO_TABS_NAME.strikeReport:
          getStrikeReportData({
            accounts: selectedAccountsAggregated,
          });
          break;

        default:
          break;
      }
    },
    [
      portfolioCurrentTab,
      getDataSpot,
      getDataPositionsAndOrders,
      getDataTransactions,
      getStrikeReportData,
      selectedAccountsAggregated,
      aggregatedAccountsByExchange,
      riskSettingsSelected,
      riskToken,
      spotOverrideValue,
      pnlRiskToken,
    ],
  );

  const handlePortfolioReCalculateAggregatedAccounts = useCallback(() => {
    if (aggregatedData.dataAccounts.length > 0 || !triggerRecalculate) return;

    handleDataFetch(portfolioCurrentTab);
    setTriggerRecalculate(false);
  }, [
    aggregatedData.dataAccounts.length,
    selectedAccountsAggregated,
    triggerRecalculate,
    portfolioCurrentTab,
    handleDataFetch,
  ]);

  const handlePortfolioCalculateAggregatedAccounts = useCallback(() => {
    if (!selectedDataDisplayOption || selectedAccountsAggregated.length === 0) return;

    if (!isAccountCalculatedAggregatedAccounts) {
      Object.keys(aggregatedAccountsByExchange).forEach((exchange) => {
        const accounts = aggregatedAccountsByExchange[exchange];
        if (accounts.length > 0) {
          getDataSpot({
            account: accounts,
            dataParent: riskTypes.spotLadder,
            riskTokenParam: riskToken,
            riskSettings: riskSettingsSelected,
            isSpotAggregatedAccounts: isSpotCalculateAggregatedAccounts,
            accountsAggregated: selectedAccountsAggregated,
          });
        }
      });

      setIsReadyToDisplayTabsForTheFirstTimeOrRecalculate(true);
      return;
    }

    if (isDataDisplayPerAccount) return;

    setIsReadyToDisplayTabsForTheFirstTimeOrRecalculate(true);
  }, [
    selectedDataDisplayOption,
    selectedAccountsAggregated,
    isAccountCalculatedAggregatedAccounts,
    isDataDisplayPerAccount,
    getDataSpot,
    riskSettingsSelected,
    clearAggregatedData,
    riskToken,
    aggregatedAccountsByExchange,
    isReadyToDisplayTabsForTheFirstTimeOrRecalculate,
  ]);

  useEffect(() => {
    if (!selectedDataDisplayOption || selectedAccountsAggregated.length === 0) return;

    setIsReadyToDisplayTabsForTheFirstTimeOrRecalculate(false);

    if (!isAccountCalculatedAggregatedAccounts) {
      setAggregatedCalculationStatus(true);

      return;
    }

    setTriggerRecalculate(true);
    clearAggregatedData();
  }, [isReadyToDisplayTabsForTheFirstTimeOrRecalculate]);

  useEffect(() => {
    if (isDataDisplayPerAccount || isPortfolioLoading) return;

    if (aggregatedData?.isCalculated) {
      setIsAccountCalculatedAggregatedAccounts(true);
    } else {
      setIsAccountCalculatedAggregatedAccounts(false);
    }
  }, [aggregatedData?.isCalculated, isPortfolioLoading, isDataDisplayPerAccount]);

  useEffect(() => {
    if (isDataDisplayPerAccount) return;

    if (triggerRecalculate && aggregatedData.dataAccounts.length === 0) {
      handlePortfolioReCalculateAggregatedAccounts();
    }
  }, [
    isDataDisplayPerAccount,
    triggerRecalculate,
    aggregatedData.dataAccounts.length,
    handlePortfolioReCalculateAggregatedAccounts,
  ]);

  return {
    handlePortfolioCalculateAggregatedAccounts,
    isAccountCalculatedAggregatedAccounts,
  };
};
